import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Customer {
  id: string;
  name: string | null;
  phone: string;
  email: string | null;
}

interface CustomerContextType {
  customer: Customer | null;
  token: string | null;
  isLoggedIn: boolean;
  login: (token: string, customer: Customer) => void;
  logout: () => void;
}

const CustomerContext = createContext<CustomerContextType | null>(null);

export function CustomerProvider({ children, subdomain }: { children: ReactNode; subdomain: string }) {
  const key = `dilzee_cust_${subdomain}`;
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(key);
      if (stored) {
        const parsed = JSON.parse(stored);
        setToken(parsed.token);
        setCustomer(parsed.customer);
      }
    } catch {}
  }, [key]);

  const login = (newToken: string, newCustomer: Customer) => {
    setToken(newToken);
    setCustomer(newCustomer);
    try { sessionStorage.setItem(key, JSON.stringify({ token: newToken, customer: newCustomer })); } catch {}
  };

  const logout = () => {
    setToken(null);
    setCustomer(null);
    try { sessionStorage.removeItem(key); } catch {}
  };

  return (
    <CustomerContext.Provider value={{ customer, token, isLoggedIn: !!token, login, logout }}>
      {children}
    </CustomerContext.Provider>
  );
}

export function useCustomer() {
  const ctx = useContext(CustomerContext);
  if (!ctx) throw new Error('useCustomer must be used inside CustomerProvider');
  return ctx;
}
